import type { ForwardRefRenderFunction, KeyboardEventHandler } from 'react';
import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import classNames from 'clsx';

import {
  activateByEnterButton,
  keyCodes,
} from '@wix/editor-elements-common-utils';
import type {
  ILoginSocialBarCustomMenu,
  ILoginSocialBarProps,
  LoginSocialBarMenuItemProps,
} from '../../../LoginSocialBar.types';
import { Alignment } from '../../../LoginSocialBar.types';
import {
  DIRECTION_LEFT_TO_RIGHT,
  DIRECTION_RIGHT_TO_LEFT,
  TEST_ID_CUSTOM_MENU,
  TEST_ID_LINK,
} from '../../../constants';
import Link from '@wix/thunderbolt-elements/src/components/Link/viewer/Link';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../../bi/events';
import styles from './LoginSocialBarCustomMenu.scss';

type ILoginSocialBarCustomMenuProps = Pick<
  ILoginSocialBarProps,
  | 'menuItems'
  | 'onLogout'
  | 'translations'
  | 'direction'
  | 'currentPrimaryPageHref'
  | 'menuItemsAlignment'
  | 'reportBi'
> & {
  ariaLabel: string;
  open: boolean;
  onClose(): void;
  reportBiOnMenuItemClick: (item: LoginSocialBarMenuItemProps) => void;
};

const queryFocusableMenuElements = (menuElement: HTMLElement) =>
  Array.from(menuElement.querySelectorAll<HTMLElement>('[tabindex],[href]'));

const LoginSocialBarCustomMenu: ForwardRefRenderFunction<
  ILoginSocialBarCustomMenu,
  ILoginSocialBarCustomMenuProps
> = (
  {
    ariaLabel,
    menuItems,
    translations,
    direction,
    currentPrimaryPageHref,
    open,
    onLogout,
    onClose,
    menuItemsAlignment,
    reportBi,
    reportBiOnMenuItemClick,
  },
  ref,
) => {
  const navRef = useRef<HTMLElement>(null);
  const className = classNames(styles.root, {
    [styles.open]: open,
    [styles.rtl]: direction === DIRECTION_RIGHT_TO_LEFT,
    [styles.ltr]: direction === DIRECTION_LEFT_TO_RIGHT,
    [styles.menuLeft]: menuItemsAlignment === Alignment.Left,
    [styles.menuCenter]: menuItemsAlignment === Alignment.Center,
    [styles.menuRight]: menuItemsAlignment === Alignment.Right,
  });

  useImperativeHandle(ref, () => ({
    focusFirstMenuItem: () => {
      if (!navRef.current) {
        return;
      }
      const [firstElement] = queryFocusableMenuElements(navRef.current);
      if (firstElement) {
        firstElement.focus();
      }
    },
  }));

  const handleKeyDown: KeyboardEventHandler = event => {
    const { keyCode } = event;
    const { escape, arrowDown, arrowUp, tab, enter } = keyCodes;
    const focusChangeKeys: Array<number> = [arrowDown, arrowUp, tab];

    if (keyCode === escape) {
      event.preventDefault();
      onClose();
    } else if (keyCode === enter) {
      event.preventDefault();
      const menuItemLink = event.target as HTMLElement;
      menuItemLink.click();
    } else if (focusChangeKeys.includes(keyCode)) {
      event.preventDefault();
      if (!navRef.current) {
        return;
      }

      const focusableElements = queryFocusableMenuElements(navRef.current);
      const currentElement = event.target as HTMLElement;
      const currentElementIndex = focusableElements.indexOf(currentElement);
      if (currentElementIndex === -1) {
        return;
      }

      let nextElementIndex = currentElementIndex;
      if (keyCode === arrowUp) {
        nextElementIndex--;
        if (nextElementIndex < 0) {
          nextElementIndex = focusableElements.length - 1;
        }
      } else {
        nextElementIndex = (nextElementIndex + 1) % focusableElements.length;
      }

      focusableElements[nextElementIndex].focus();
    }
  };

  const onLinkClick = (item: LoginSocialBarMenuItemProps) => {
    reportBiOnMenuItemClick(item);
    void reportBi(
      getMembersAreaNavigationEvent({
        action: BIAction.ItemClick,
        pageName: item.label,
      }),
      {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      },
    );
  };

  const handleLogout = () => {
    reportBiOnMenuItemClick({ label: translations.logOutText!, link: {} });
    onLogout();
  };

  return (
    <nav
      ref={navRef}
      className={className}
      aria-label={ariaLabel}
      aria-live="polite"
      role="menu"
      data-testid={TEST_ID_CUSTOM_MENU}
      onKeyDown={handleKeyDown}
      onClick={onClose}
    >
      <ul className={styles.menuList}>
        {menuItems.map((item, index) => (
          <li key={index}>
            <Link
              role="menuitem"
              onClick={() => onLinkClick(item)}
              {...item.link}
              dataTestId={TEST_ID_LINK}
              className={classNames(styles.link, {
                [styles.selected]:
                  !!item.link.href && item.link.href === currentPrimaryPageHref,
              })}
            >
              <span className={styles.label}>{item.label}</span>
              {typeof item.displayCount === 'number' && (
                <span className={styles.displayCount}>
                  ({item.displayCount})
                </span>
              )}
            </Link>
          </li>
        ))}
        {!!menuItems.length && (
          <li role="separator">
            <hr />
          </li>
        )}
        <li>
          <div
            role="menuitem"
            className={styles.link}
            onClick={handleLogout}
            tabIndex={0}
            data-testid={TEST_ID_LINK}
            onKeyDown={activateByEnterButton}
          >
            <span className={styles.label}>{translations.logOutText}</span>
          </div>
        </li>
      </ul>
    </nav>
  );
};

export default forwardRef(LoginSocialBarCustomMenu);
